import React, {useContext, useEffect, useRef, useState} from 'react';
import ProductReviews from './productReviews';
import {getProductReviewCounts, getProductReviews, getReviewProduct} from '../../../util/requestSender';
import {AppContext} from '../../misc/appContext';
import {Navigate, useParams} from 'react-router';
import {ResponseData} from '@dvrd/dvr-controls';
import {createErrorMessage} from '../../../util/utils';
import {BolReviewSorting, ReviewProduct} from '../../../util/interfaces';
import {Rating} from '../../../util/types';
import ProductReview from '../../../models/productReviewModel';
import {DefaultNumberObject} from '../../../util/defaultObject';
import ProductReviewsAnalyseController, {ReviewAnalysisHandle} from './analyse/productReviewsAnalyseController';
import {SubscriptionModuleName} from '../../../util/enums';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const LIMIT = 20;
new DefaultNumberObject();

function getInitialFilters(): Set<Rating> {
    const params = new URLSearchParams(location.search);
    const filterRating = params.get('rating');
    if (filterRating === 'positive') return new Set([4, 5]);
    else if (filterRating === 'negative') return new Set([1, 2, 3]);
    return new Set();
}

export default function ProductReviewsController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {t} = useTranslation();
    const {ean} = useParams<{ean: string}>();
    const [mounted, setMounted] = useState(false);
    const [product, setProduct] = useState<ReviewProduct | null>(null);
    const [reviews, setReviews] = useState<Array<ProductReview>>([]);
    const [sorting, setSorting] = useState<BolReviewSorting>(BolReviewSorting.MOST_RECENT);
    const [loadingProduct, setLoadingProduct] = useState(true);
    const [loadingRatings, setLoadingRatings] = useState(true);
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [reviewCounts, setReviewCounts] = useState(new DefaultNumberObject<Rating>());
    const [selectedRatings, setSelectedRatings] = useState<Set<Rating>>(getInitialFilters());
    const [total, setTotal] = useState(0);
    const listPage = useRef(0);
    const loadingMore = useRef(false);
    const abortController = useRef(new AbortController());
    const analysisRef = useRef<ReviewAnalysisHandle>(null);

    useEffect(() => {
        if (!ean) return;
        if (mounted) loadReviews();
        setMounted(true);
    }, [sorting, selectedRatings]);

    useEffect(() => {
        if (!ean) return;
        loadProductRatings();
        loadProduct();
        window.scrollTo({top: 0, behavior: 'smooth'});
        checkMountParams();

        return function () {
            abortController.current.abort();
        };
    }, []);

    if (!ean) return <Navigate to='/review-analytics' />;

    function onChangeSorting(value: BolReviewSorting) {
        if (sorting === value) return;
        listPage.current = 0;
        setSorting(value);
    }

    function onClickBack() {
        history.back();
    }

    function onSelectRating(value: Rating) {
        return () => {
            listPage.current = 0;
            const selected = new Set(selectedRatings);
            if (selected.has(value)) selected.delete(value);
            else selected.add(value);
            setSelectedRatings(selected);
        };
    }

    function onLoadMore(callback: VoidFunction) {
        if (!account || loadingMore.current) return;
        loadingMore.current = true;
        listPage.current += 1;
        getProductReviews({
            abortController: abortController.current,
            data: {
                ean: ean!,
                account_id: account.id,
                limit: LIMIT,
                page: listPage.current,
                sort_type: sorting,
                filter_1_star: selectedRatings.has(1),
                filter_2_star: selectedRatings.has(2),
                filter_3_star: selectedRatings.has(3),
                filter_4_star: selectedRatings.has(4),
                filter_5_star: selectedRatings.has(5)
            },
            callback: (data: ResponseData) => {
                setLoadingReviews(false);
                if (data.success) {
                    const _reviews = data.reviews.map((rev: any) => new ProductReview(rev));
                    setReviews(current => current.concat(_reviews));
                    loadingMore.current = false;
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'reviewAnalytics.productReviews.noReviewsFound',
                                    'Er kunnen geen reviews gevonden worden.'
                                )
                        )
                    );
                window.setTimeout(callback);
            }
        });
    }

    function loadReviews(checkAnalysis: boolean = false) {
        if (!account) return;
        setLoadingReviews(true);
        getProductReviews({
            abortController: abortController.current,
            data: {
                ean: ean!,
                account_id: account.id,
                limit: LIMIT,
                page: listPage.current,
                sort_type: sorting,
                filter_5_star: selectedRatings.has(5),
                filter_4_star: selectedRatings.has(4),
                filter_3_star: selectedRatings.has(3),
                filter_2_star: selectedRatings.has(2),
                filter_1_star: selectedRatings.has(1)
            },
            callback: (data: ResponseData) => {
                setLoadingReviews(false);
                if (data.success) {
                    const _reviews = data.reviews.map((rev: any) => new ProductReview(rev));
                    setReviews(_reviews);
                    setTotal(data.total);
                    if (checkAnalysis) checkParams();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'reviewAnalytics.productReviews.noReviewsFound',
                                    'Er kunnen geen reviews gevonden worden.'
                                )
                        )
                    );
            }
        });
    }

    function loadProductRatings() {
        if (!account) return;
        setLoadingRatings(true);
        getProductReviewCounts({
            abortController: abortController.current,
            data: {ean: ean!, account_id: account.id, limit: '20', sort_type: sorting},
            callback: (data: ResponseData) => {
                setLoadingRatings(false);
                if (data.success) {
                    const {review_product} = data;
                    setReviewCounts(
                        new DefaultNumberObject<Rating>({
                            1: review_product.rating_one,
                            2: review_product.rating_two,
                            3: review_product.rating_three,
                            4: review_product.rating_four,
                            5: review_product.rating_five
                        })
                    );
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'reviewAnalytics.productReviews.loadCountsFailed',
                                    'Het laden van de review aantallen is niet gelukt.'
                                )
                        ),
                        t('reviewAnalytics.productReviews.loadFailed', 'Laden mislukt')
                    );
            }
        });
    }

    function loadProduct() {
        const {account} = context.accountContext;
        if (!account) return;
        setLoadingProduct(true);
        getReviewProduct({
            abortController: abortController.current,
            account_id: account.id,
            ean: ean!,
            callback: (data: ResponseData) => {
                setLoadingProduct(false);
                if (data.success) {
                    setProduct(data.product);
                    loadReviews(true);
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'reviewAnalytics.productReviews.loadProductsFailed',
                                    'Het laden van de producten is niet gelukt.'
                                )
                        ),
                        t('reviewAnalytics.productReviews.loadFailed', 'Laden mislukt')
                    );
            }
        });
    }

    function checkParams() {
        const params = new URLSearchParams(location.search);
        if (params.get('analyse') === '1') analysisRef.current?.analyse();
    }

    function checkMountParams() {
        if (!user?.subscription?.hasActiveModule(SubscriptionModuleName.NOTIFICATIONS)) return;
        const params = new URLSearchParams(location.search);
        if (params.get('analyse') === '1') analysisRef.current?.setLoading(true);
    }

    return (
        <div className='product-reviews-page'>
            <ProductReviews
                onChangeSorting={onChangeSorting}
                onLoadMore={onLoadMore}
                onClickBack={onClickBack}
                onSelectRating={onSelectRating}
                sortingValue={sorting}
                loadingProduct={loadingProduct}
                loadingRatings={loadingRatings}
                loadingReviews={loadingReviews}
                reviews={reviews}
                product={product}
                reviewCounts={reviewCounts}
                total={total}
                selectedRatings={selectedRatings}
            />
            <ProductReviewsAnalyseController ean={ean} abortController={abortController.current} ref={analysisRef} />
        </div>
    );
}
